import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiPermissionService, PermissionService } from '@innovation-os-ng/core/core';
import { LogService } from '@innovation-os-ng/core/logger';
import { skipper, SpaceSelectors } from '@innovation-os-ng/core/state';
import { ToastNotificationsService, ToastType } from '@innovation-os-ng/core/toast-notifications';
import { LoaderModule } from '@innovation-os-ng/ui/loader';
import { CompoundFilter, EntityFilter, Filter, FilterFactory } from '@itonics/filters';
import { TranslateService } from '@itonics/ngx-translate-simple';
import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { Subject, Unsubscribable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { FeedWidgetService } from '../../contracts/feed-widget.service';
import { EntityFragment } from '../../interfaces/entity';
import { SearchField, SearchFieldIdentifiers, SearchFieldQueryParams, SearchFieldState } from '../../interfaces/feed';

@Component({
  selector: 'signals-search-field-selector-v2',
  templateUrl: './search-field-selector.component.html',
  styleUrls: ['./search-field-selector.component.scss'],
  standalone: true,
  imports: [CommonModule, LoaderModule, NgSelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: PermissionService, useClass: ApiPermissionService }]
})
export class SearchFieldSelectorV2Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  @Output()
  public onSearchFieldSelected: EventEmitter<SearchField> = new EventEmitter<SearchField>();

  @Output()
  public onLinkedSearchFieldCreated: EventEmitter<SearchField> = new EventEmitter<SearchField>();

  /*
    We need to output when the preset selector is ready
    and when there is no presets to show we can use
    this info to show/hide content outside
  */
  @Output()
  public ready: EventEmitter<Array<SearchField>> = new EventEmitter<Array<SearchField>>();

  @Input()
  public disableSearchFieldPicker: boolean;

  @Input()
  public subscriptionToInsights: boolean;

  @Input()
  set filter(filter: Filter) {
    this.elementsFromFilter = [];
    this.searchFields = [];
    this.paginationCursor = null;
    if (filter) {
      this.documentFilter = filter;
      this.elementsFromFilter = this.extractElementUriFromFilter(this.documentFilter);
    }
    this.unSubscribeFilterChange();
    this.subscribeFilterChange();
    this.loadSearchFields();
  }

  // Element (when shown in element's detail)
  @Input()
  public element: EntityFragment;

  // Linked Search Field
  @Input()
  public linkedSearchField: SearchField;

  @Input()
  public onlyShowExplicitSearchFields: boolean;

  // Associated Search Fields
  public searchFields: Array<SearchField> = [];
  public selectedSearchField: SearchField;
  public isSearchFieldLoading: boolean = true;

  public hasEndReached: boolean;
  private elementsFromFilter: Array<string> = [];
  private sfLoaderSubscription: Unsubscribable;
  private documentsFilterChanges: Unsubscribable;
  private documentFilter: Filter;
  private paginationCursor: { [key: string]: any };
  public typeAheadSub: Subject<string> = new Subject<string>();
  public searchTerm: string;
  private onDestroy: Subject<void> = new Subject<void>();

  constructor(
    private readonly feedWidgetService: FeedWidgetService,
    private readonly cdr: ChangeDetectorRef,
    private readonly logService: LogService,
    private readonly toastNotificationsService: ToastNotificationsService,
    private readonly translate: TranslateService,
    private readonly store: Store
  ) {}

  public resetSelectedSearchField() {
    this.selectedSearchField = null;
  }

  public unSubscribeFilterChange() {
    if (this.documentsFilterChanges) {
      this.documentsFilterChanges.unsubscribe();
    }
  }

  public subscribeFilterChange() {
    this.documentsFilterChanges = this.documentFilter.onChanged(() => {
      this.hasEndReached = false;
      this.elementsFromFilter = this.extractElementUriFromFilter(this.documentFilter);
      this.paginationCursor = null;
      this.searchFields = [];
      this.loadSearchFields();
    });
  }

  async ngOnInit() {
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.searchFields = [];

          this.hasEndReached = false;
          this.paginationCursor = null;
          this.loadSearchFields();
        }
      });

    this.initiateTypeAhead();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
    this.unSubscribeFilterChange();
    this.unsubscribeSfLoader();
  }

  /**
   *
   * Extract element uris from a filter
   * If filter is entity filter simply getUri will return element uri
   * If compound filter, we need to reccurse until we get all element uri
   */
  private extractElementUriFromFilter(filter): Array<string> {
    let elementUris: Array<string> = [];
    if (filter instanceof EntityFilter) {
      elementUris.push(filter.getUri());
      return elementUris;
    }
    if (filter instanceof CompoundFilter) {
      filter.getQueries().forEach((f) => {
        const uris = this.extractElementUriFromFilter(f);
        elementUris = [...elementUris, ...uris];
      });
    }
    return elementUris;
  }

  public unsubscribeSfLoader() {
    if (this.sfLoaderSubscription) {
      this.sfLoaderSubscription.unsubscribe();
    }
  }

  /**
   * If and only if filter query provided is entity filter we need to filter out search field
   * Entity filter may also be inside compound filter
   * Filtered search field must contain element uri in its search field query or its attached entity property
   */
  private async loadSearchFields() {
    this.unsubscribeSfLoader();

    this.isSearchFieldLoading = true;
    this.cdr.detectChanges();
    const searchFieldQueryParams: SearchFieldQueryParams = {};
    if (this.paginationCursor) {
      searchFieldQueryParams.paginationCursor = JSON.stringify(this.paginationCursor);
    }

    // If this component is being rendered in an Element's view, find associated searchFields for that element
    if (this.element) {
      searchFieldQueryParams.associatedElements = this.element.uri;
    } else if (this.elementsFromFilter.length) {
      searchFieldQueryParams.associatedElements = this.elementsFromFilter.join(',');
    }

    if (this.searchTerm) {
      searchFieldQueryParams.title = this.searchTerm;
    }

    if (this.onlyShowExplicitSearchFields) {
      searchFieldQueryParams.state = SearchFieldState.EXPLICIT; // Don't allow implicit/refined searchFields to be selected
    }

    this.sfLoaderSubscription = this.feedWidgetService.getSearchFields(searchFieldQueryParams).subscribe({
      next: (results: { items: Array<SearchField>; paginationCursor?: { [key: string]: any } }) => {
        //  If we have elements from filters we need to filter our result of sf which contain those elements
        const searchFields: Array<SearchField> = results.items;
        this.mapSearchFieldWithMetadataFromQuery(searchFields);

        if (this.paginationCursor) {
          this.searchFields = [...this.searchFields, ...searchFields];
        } else {
          this.searchFields = searchFields;
        }
        if (results.paginationCursor && results.items.length > 0) {
          this.hasEndReached = false;
          this.paginationCursor = results.paginationCursor;
        } else {
          this.hasEndReached = true;
          this.paginationCursor = null;
        }
        this.isSearchFieldLoading = false;
        this.cdr.detectChanges();

        // We output that the component is ready
        this.ready.emit(this.searchFields);
      },
      error: (e: unknown) => {
        // this.isSearchFieldLoading = false;
        // Log any error occurred while loading feed
        this.logService.error('Failed to load search fields', { error: e });
        this.toastNotificationsService.showToast({
          duration: 5000,
          content: this.translate.instant('newsFeedWidget.error.generic'),
          type: ToastType.ERROR
        });
        this.cdr.detectChanges();
      }
    });
  }

  // We need to extract published Date and document type from sf
  private mapSearchFieldWithMetadataFromQuery(searchFields: Array<SearchField>) {
    searchFields.forEach((searchField) => {
      if (!searchField.query) {
        return;
      }
      const sfAsFilter: Filter = FilterFactory.fromJson(searchField.query);
      if (!(sfAsFilter instanceof CompoundFilter)) {
        return;
      }
      // Only context query can have published date or document type
      const contextQuery = sfAsFilter.getQuery(SearchFieldIdentifiers.CONTEXT);
      if (!(contextQuery instanceof CompoundFilter)) {
        return;
      }
    });
  }

  public updateSearchField(searchField: SearchField) {
    if (!searchField) {
      this.onSearchFieldSelected.emit(null);
      return;
    }
    this.onSearchFieldSelected.emit(searchField);
  }

  public scrollToEnd() {
    if (!this.hasEndReached) {
      this.loadSearchFields();
    }
  }

  private initiateTypeAhead() {
    this.typeAheadSub
      .pipe(
        map((textValue: string) => {
          this.searchTerm = textValue;
          return textValue;
        }),
        debounceTime(500)
      )
      .subscribe((textValue: string) => {
        this.paginationCursor = null;
        this.loadSearchFields();
      });
  }
}
